import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatDistanceToNow } from "date-fns";
import { tr } from "date-fns/locale";
import { Mail, Eye, Trash2, Check } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { createClient } from '@supabase/supabase-js';

// Veri yapıları
interface Message {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone?: string;
  message: string;
  web3forms_submission_id?: string;
  created_at: Date;
  read: boolean; // Veritabanından gelecek
}

// Supabase'den gelen veri yapısı
interface ContactMessageRow {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone?: string;
  message?: string;
  web3forms_submission_id?: string;
  created_at: string;
  read?: boolean; // Veritabanından gelecek
}

// Supabase bağlantısı için gerekli bilgiler
const SUPABASE_URL = "https://zvmixzvxrkjyoussyuuq.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp2bWl4enZ4cmtqeW91c3N5dXVxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDY3MTQ2ODEsImV4cCI6MjA2MjI5MDY4MX0.i2KWdzVLx_zFqteiX7SVy-TdnaoEhM--Je4_9ocX9fs";

export default function MessagesIndex() {
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  
  // Veritabanından mesajları çek
  useEffect(() => {
    async function fetchMessages() {
      try {
        setLoading(true);
        
        // Supabase istemcisini doğrudan oluştur
        const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
        
        const { data, error } = await (supabase as any)
          .from('contact_messages')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          throw error;
        }
        
        if (data) {
          const formattedMessages = data.map((msg: ContactMessageRow) => ({
            id: msg.id,
            name: msg.name,
            surname: msg.surname,
            email: msg.email,
            phone: msg.phone,
            message: msg.message || "",
            web3forms_submission_id: msg.web3forms_submission_id,
            created_at: new Date(msg.created_at),
            read: Boolean(msg.read) // Veritabanından gelen değeri kullan
          }));
          setMessages(formattedMessages);
        }
      } catch (error) {
        console.error('Mesajlar çekilirken hata:', error);
        toast({
          title: "Hata",
          description: "Mesajlar yüklenirken bir sorun oluştu.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    }
    
    fetchMessages();
  }, [toast]);
  
  // Mesajı görüntüle ve okundu olarak işaretle
  const viewMessage = async (message: Message) => {
    setSelectedMessage({...message, read: true});
    
    if (!message.read) {
      try {
        // Supabase istemcisini doğrudan oluştur
        const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
        
        const { error } = await (supabase as any)
          .from('contact_messages')
          .update({ read: true })
          .eq('id', message.id);
        
        if (error) {
          throw error;
        }
        
        // Mesajları güncelle
        setMessages(prev => 
          prev.map(msg => msg.id === message.id ? {...msg, read: true} : msg)
        );
      } catch (error) {
        console.error('Mesaj okundu olarak işaretlenirken hata:', error);
        toast({
          title: "Hata",
          description: "Mesaj durumu güncellenirken bir sorun oluştu.",
          variant: "destructive"
        });
      }
    }
  };
  
  // Mesajı okundu/okunmadı olarak işaretle
  const toggleReadStatus = async (message: Message) => {
    try {
      const newReadStatus = !message.read;
      
      // Supabase istemcisini doğrudan oluştur
      const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
      
      const { error } = await (supabase as any)
        .from('contact_messages')
        .update({ read: newReadStatus })
        .eq('id', message.id);
      
      if (error) {
        throw error;
      }
      
      // Mesajları güncelle
      setMessages(prev => 
        prev.map(msg => msg.id === message.id ? {...msg, read: newReadStatus} : msg)
      );
      
      toast({
        title: newReadStatus ? "Okundu olarak işaretlendi" : "Okunmadı olarak işaretlendi",
        description: `"${message.name} ${message.surname}" mesajı ${newReadStatus ? "okundu" : "okunmadı"} olarak işaretlendi.`,
      });
    } catch (error) {
      console.error('Mesaj durumu güncellenirken hata:', error);
      toast({
        title: "Hata",
        description: "Mesaj durumu güncellenirken bir sorun oluştu.",
        variant: "destructive"
      });
    }
  };
  
  // Mesajı sil
  const deleteMessage = async (id: number) => {
    if (window.confirm("Bu mesajı silmek istediğinize emin misiniz?")) {
      try {
        // Supabase istemcisini doğrudan oluştur
        const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
        
        const { error } = await (supabase as any)
          .from('contact_messages')
          .delete()
          .eq('id', id);
        
        if (error) {
          throw error;
        }
        
        // Mesajları güncelle
        setMessages(prev => prev.filter(msg => msg.id !== id));
        
        // Eğer açık olan mesaj siliniyorsa, modalı kapat
        if (selectedMessage && selectedMessage.id === id) {
          setSelectedMessage(null);
        }
        
        toast({
          title: "Mesaj silindi",
          description: "Mesaj başarıyla silindi."
        });
      } catch (error) {
        console.error('Mesaj silinirken hata:', error);
        toast({
          title: "Hata",
          description: "Mesaj silinirken bir sorun oluştu.",
          variant: "destructive"
        });
      }
    }
  };
  
  const closeDialog = () => {
    setSelectedMessage(null);
  };
  
  const handleReply = (messageId: number) => {
    // This would open an email client or a reply form in a real application
    window.open(`mailto:${selectedMessage?.email}?subject=Yanıt: İletişim Formu Mesajı`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Mesajlar</h1>
            <p className="text-muted-foreground">İletişim formundan gelen mesajlar</p>
          </div>
        </div>
        
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Gönderen</TableHead>
                <TableHead className="hidden md:table-cell">E-posta</TableHead>
                <TableHead className="hidden md:table-cell">Tarih</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6">
                    Mesajlar yükleniyor...
                  </TableCell>
                </TableRow>
              ) : messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6">
                    Henüz mesaj bulunmuyor
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((message) => (
                  <TableRow key={message.id}>
                    <TableCell className="font-medium">{message.name} {message.surname}</TableCell>
                    <TableCell className="hidden md:table-cell">{message.email}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {formatDistanceToNow(message.created_at, { locale: tr, addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      {message.read ? (
                        <Badge variant="outline" className="cursor-pointer" onClick={() => toggleReadStatus(message)}>Okundu</Badge>
                      ) : (
                        <Badge className="cursor-pointer" onClick={() => toggleReadStatus(message)}>Yeni</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => viewMessage(message)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteMessage(message.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      
      <Dialog open={!!selectedMessage} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>İletişim Formu Mesajı</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-4 gap-2 text-sm">
              <span className="text-muted-foreground">Gönderen:</span>
              <span className="col-span-3 font-medium">{selectedMessage?.name} {selectedMessage?.surname}</span>
              
              <span className="text-muted-foreground">Email:</span>
              <span className="col-span-3">{selectedMessage?.email}</span>
              
              {selectedMessage?.phone && (
                <>
                  <span className="text-muted-foreground">Telefon:</span>
                  <span className="col-span-3">{selectedMessage?.phone}</span>
                </>
              )}
              
              <span className="text-muted-foreground">Tarih:</span>
              <span className="col-span-3">
                {selectedMessage?.created_at.toLocaleDateString('tr-TR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
              
              {selectedMessage?.web3forms_submission_id && (
                <>
                  <span className="text-muted-foreground">Form ID:</span>
                  <span className="col-span-3">{selectedMessage?.web3forms_submission_id}</span>
                </>
              )}
            </div>
            
            <div className="rounded-md border p-4 mt-4">
              <p className="whitespace-pre-line">{selectedMessage?.message}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Kapat
            </Button>
            <Button variant="destructive" onClick={() => selectedMessage && deleteMessage(selectedMessage.id)}>
              <Trash2 className="mr-2 h-4 w-4" />
              Sil
            </Button>
            <Button onClick={() => handleReply(selectedMessage?.id || 0)}>
              <Mail className="mr-2 h-4 w-4" />
              Yanıtla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
